import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  LockClosedIcon,
  GiftIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { api, type CourseDetail, type PlaybackResponse } from '@/lib/api';
import { lessonView } from '@/lib/lessonView';
import { soleModule } from '@/lib/courseView';
import { Player } from '@/components/Player';
import { Button } from '@/components/ui/button';

export default function Lesson() {
  const { slug = '', moduleId = '', lessonId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const startAt = params.get('t') ? Number(params.get('t')) : undefined;

  const courseQ = useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.get<CourseDetail>(`/api/courses/${slug}`),
    enabled: slug.length > 0,
    // A missing or unentitled course is a verdict, not a hiccup — same rule the
    // module page uses, so a deep link into a lesson fails as fast as one into
    // the module above it.
    retry: (count, err) => {
      const msg = (err as Error).message;
      if (msg === 'course_locked' || msg === 'course_not_found') return false;
      return count < 2;
    },
  });

  const playbackQ = useQuery({
    queryKey: ['playback', lessonId],
    queryFn: () => api.get<PlaybackResponse>(`/api/course/lessons/${lessonId}/playback`),
    enabled: lessonId.length > 0,
    staleTime: 60_000,
    // A drip-locked lesson returns 403 'previous_lesson_incomplete' — that's a
    // deliberate state, not a transient failure, so don't retry it.
    retry: (count, err) =>
      (err as Error).message !== 'previous_lesson_incomplete' && count < 2,
  });

  // Called when the player decides the lesson is finished (last 10s / ended).
  // Optimistically flip THIS lesson to completed in the cached course detail so
  // the drip "Weiter" button + checkmark unlock immediately, then refetch to
  // reconcile with the server (which the player has already written to).
  const handleCompletion = useCallback(() => {
    qc.setQueryData<CourseDetail>(['course', slug], (old) => {
      if (!old) return old;
      const now = Math.floor(Date.now() / 1000);
      return {
        ...old,
        modules: old.modules.map((m) => ({
          ...m,
          lessons: m.lessons.map((l) =>
            l.id === lessonId
              ? {
                  ...l,
                  progress: {
                    ...l.progress,
                    completed: true,
                    completedAt: l.progress.completedAt || now,
                  },
                }
              : l,
          ),
        })),
      };
    });
    void qc.invalidateQueries({ queryKey: ['course', slug] });
    void qc.invalidateQueries({ queryKey: ['courses'] });
  }, [qc, slug, lessonId]);

  const { module, lesson, lessonNumber, prev: prevLesson, next: nextLesson } = useMemo(
    () => lessonView(courseQ.data?.modules ?? [], moduleId, lessonId),
    [courseQ.data, moduleId, lessonId],
  );

  // One level up is the module this lesson sits in — the surface the learner
  // came from. Two cases keep the old course-page target: a course that
  // collapses its single module onto the course page (there the module page is
  // a level nobody has seen), and an unresolved module (bad id, or the course
  // never loaded). Without a slug there is nothing above but the start page.
  const toModule = !!module && !soleModule(courseQ.data?.modules ?? []);
  const backTo = toModule ? `/kurse/${slug}/${module.id}` : slug ? `/kurse/${slug}` : '/';
  const backLabel = toModule ? 'Zurück zum Modul' : 'Zurück zum Kurs';

  if (courseQ.isLoading || playbackQ.isLoading) {
    return <div className="mx-auto max-w-3xl p-6 text-muted-foreground">Lädt …</div>;
  }
  // No cookie / session (e.g. a shared lesson link opened logged-out) → the data
  // APIs 401. Send them to the login screen (which offers the email-only soft
  // login for the free course) instead of a dead "not found" / "failed" error.
  const courseErr = (courseQ.error as Error | null)?.message;
  const authErr =
    courseErr === 'not_authenticated' ||
    (playbackQ.error as Error | null)?.message === 'not_authenticated';
  if (authErr) return <Navigate to="/login" replace />;
  // The course itself is gone or not theirs, so neither the module nor the
  // course page below would render anything — only the start page is real.
  if (courseErr === 'course_locked' || courseErr === 'course_not_found') {
    return <Navigate to="/" replace />;
  }
  if (!module || !lesson) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="text-destructive">Lektion nicht gefunden.</p>
        <Button variant="outline" onClick={() => navigate(backTo)} className="mt-4">
          {backLabel}
        </Button>
      </div>
    );
  }
  if (!playbackQ.data) {
    const msg = playbackQ.error ? (playbackQ.error as Error).message : '';
    if (msg === 'previous_lesson_incomplete') {
      return (
        <div className="mx-auto max-w-3xl p-6">
          <div className="flex flex-col items-center gap-4 rounded-lg border bg-muted/30 p-8 text-center">
            <LockClosedIcon className="h-8 w-8 text-muted-foreground" />
            <div className="space-y-1">
              <h1 className="text-lg font-semibold">Diese Lektion ist noch gesperrt</h1>
              <p className="text-sm text-muted-foreground">
                Bitte sieh dir die vorherigen Lektionen nacheinander vollständig an. Sobald du die
                vorherige Lektion abgeschlossen hast, wird diese automatisch freigeschaltet.
              </p>
            </div>
            {/* Same level up as the header link. The lesson that unlocks this
                one is usually in this very module, one tap away; on the first
                lesson of a module it sits in the previous one, and the module
                page's own back-link is the next step. */}
            <Button variant="outline" onClick={() => navigate(backTo)}>
              {backLabel}
            </Button>
          </div>
        </div>
      );
    }
    return <div className="mx-auto max-w-3xl p-6 text-destructive">Video konnte nicht geladen werden.</div>;
  }

  const sequential = !!courseQ.data?.course.sequentialUnlock;
  // In a drip course you can't advance until the current lesson is completed.
  const nextLockedBySeq = sequential && !!nextLesson && !lesson.progress.completed;

  // On the LAST lesson of the bonus-gating course, the "next" slot becomes a
  // "Bonus freischalten" CTA instead of a dead "Letzte Lektion" button. In a
  // drip course it stays gated until this final lesson is completed — which is
  // also the moment that completes the course and unlocks the bonus. The CTA
  // sends them home, where the now-unlocked bonus card lives.
  const bonusCtaHere = !nextLesson && !!courseQ.data?.course.bonusUnlock;
  const bonusLockedBySeq = bonusCtaHere && sequential && !lesson.progress.completed;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-32 sm:p-6 sm:pb-32">
      <div className="flex items-center justify-between">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="h-4 w-4" /> {backLabel}
        </Link>
        <span className="text-xs text-muted-foreground">
          Lektion {lessonNumber} / {module.lessons.length}
        </span>
      </div>

      <header className="space-y-3">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight break-words hyphens-auto">
          {lesson.title}
        </h1>
        {lesson.progress.completed && (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success">
            <CheckCircleIcon className="h-4 w-4" />
            Abgeschlossen
          </span>
        )}
      </header>

      <Player
        lessonId={lesson.id}
        iframeUrl={playbackQ.data.iframeUrl}
        durationSeconds={playbackQ.data.durationSeconds}
        initialPosition={lesson.progress.lastPositionSeconds}
        initialFurthest={lesson.progress.furthestSeconds}
        chapters={playbackQ.data.chapters}
        ctaUrl={playbackQ.data.ctaUrl}
        ctaLabel={playbackQ.data.ctaLabel}
        startAt={startAt}
        onCompletion={handleCompletion}
      />

      <BookmarkToggle slug={slug} lessonId={lesson.id} bookmarked={lesson.bookmarked} />

      {lesson.aiSummary && (
        <section className="space-y-2 rounded-lg border bg-muted/40 p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Zusammenfassung
          </h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
            {lesson.aiSummary}
          </p>
        </section>
      )}

      {lesson.description && (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
          {lesson.description}
        </p>
      )}

      {lesson.resources.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Materialien
          </h2>
          <ul className="space-y-2">
            {lesson.resources.map((r) => (
              <li key={r.id}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-md border bg-card p-3 text-sm transition hover:border-primary/40"
                >
                  {r.kind === 'pdf' ? <DocumentTextIcon className="h-4 w-4" /> : <ArrowTopRightOnSquareIcon className="h-4 w-4" />}
                  <span className="flex-1 truncate">{r.label || r.url}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto max-w-3xl space-y-1.5">
          {(nextLockedBySeq || bonusLockedBySeq) && (
            <p className="text-center text-xs text-muted-foreground">
              {bonusLockedBySeq
                ? 'Sieh dir das Video bis zum Ende an, um deinen Bonus freizuschalten.'
                : 'Sieh dir das Video bis zum Ende an, um die nächste Lektion freizuschalten.'}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!prevLesson}
              onClick={() =>
                prevLesson && navigate(`/kurse/${slug}/${prevLesson.moduleId}/${prevLesson.lessonId}`)
              }
              className="flex-1"
            >
              <ChevronLeftIcon className="h-4 w-4" /> Zurück
            </Button>
            <Button
              disabled={bonusCtaHere ? bonusLockedBySeq : !nextLesson || nextLockedBySeq}
              onClick={() => {
                if (bonusCtaHere) {
                  navigate('/');
                } else if (nextLesson) {
                  navigate(`/kurse/${slug}/${nextLesson.moduleId}/${nextLesson.lessonId}`);
                }
              }}
              className="flex-1"
            >
              {bonusCtaHere ? (
                bonusLockedBySeq ? (
                  <><LockClosedIcon className="h-4 w-4" /> Gesperrt</>
                ) : (
                  <><GiftIcon className="h-4 w-4" /> Bonus freischalten</>
                )
              ) : !nextLesson ? (
                'Letzte Lektion'
              ) : nextLockedBySeq ? (
                <><LockClosedIcon className="h-4 w-4" /> Gesperrt</>
              ) : (
                <>Weiter <ChevronRightIcon className="h-4 w-4" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Bookmark toggle. Sits below the player rather than over it — the Bunny
 * iframe owns its own overlay chrome and an absolutely-positioned control
 * fights with it.
 *
 * The optimistic write targets the cached course detail, which is the same
 * object that supplied `bookmarked`, so the icon flips on tap. ['bookmarks']
 * is invalidated rather than patched: the start page and the Lesezeichen page
 * both read it, and neither is on screen here.
 */
function BookmarkToggle({
  slug,
  lessonId,
  bookmarked,
}: {
  slug: string;
  lessonId: string;
  bookmarked: boolean;
}) {
  const qc = useQueryClient();

  const setCached = (next: boolean) => {
    qc.setQueryData<CourseDetail>(['course', slug], (old) =>
      old
        ? {
            ...old,
            modules: old.modules.map((m) => ({
              ...m,
              lessons: m.lessons.map((l) =>
                l.id === lessonId ? { ...l, bookmarked: next } : l,
              ),
            })),
          }
        : old,
    );
  };

  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      api.post(`/api/course/lessons/${lessonId}/${next ? 'bookmark' : 'unbookmark'}`),
    onMutate: (next: boolean) => {
      setCached(next);
      return { previous: bookmarked };
    },
    onError: (_err, _next, ctx) => {
      if (ctx) setCached(ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });

  return (
    <button
      type="button"
      onClick={() => mutation.mutate(!bookmarked)}
      aria-pressed={bookmarked}
      className={
        bookmarked
          ? 'inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/12 px-4 py-2 text-sm font-medium text-primary-strong transition-colors'
          : 'inline-flex items-center gap-2 rounded-full border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground'
      }
    >
      {bookmarked ? (
        <BookmarkSolidIcon className="h-4 w-4" />
      ) : (
        <BookmarkIcon className="h-4 w-4" />
      )}
      {bookmarked ? 'Gemerkt' : 'Merken'}
    </button>
  );
}
